import { useMemo } from 'react';
import { FilterGroup } from '../components/GenericFilterSection';

const FILTER_COMPONENT_MAP = {
  'ราคา': 'price',
  'ช่วงราคา': 'price', 
  'ค่าเช่า/เดือน': 'price',
  'ห้องนอน': 'bedroom',
  'ห้องน้ำ': 'bathroom',
  'พื้นที่': 'area',
  'ขนาดพื้นที่': 'area',
  'โควต้า': 'quota',
  'การถือครอง': 'quota',
  'สถานะ': 'status',
  'ชั้น': 'floor',
  'วิว': 'view',
  'ทิศ': 'direction',
  'ประกาศโดย': 'publishedBy',
  'ลงประกาศเมื่อ': 'postedDate',
  'ระยะห่าง': 'distance'
} as const;

export type FilterComponentType = typeof FILTER_COMPONENT_MAP[keyof typeof FILTER_COMPONENT_MAP] | 'generic';

export interface DynamicFilter {
  group: FilterGroup;
  componentType: FilterComponentType;
  order: number;
}

const GENERIC_FILTER_LAYOUTS = {
  'ระยะห่าง': 'single',  // ปกติจะมีไม่กี่ตัวเลือก
  'รูปแบบ': 'single',     // ปกติจะมีไม่กี่ตัวเลือก
  'ประกาศโดย': 'single',  // มี 4 ตัวเลือก
} as const;

interface UseDynamicFiltersReturn {
  dynamicFilters: DynamicFilter[];
  getFilterLayout: (groupName: string) => 'single' | 'double';
}

export const useDynamicFilters = (
  filtersData: FilterGroup[],
  fallbackFilters: string[] = [],
  selectedCategory?: string | null,
  selectedLocation?: any,
  selectedSubLocation?: any,
  locationTypeId?: number | null
): UseDynamicFiltersReturn => {
  
  const shouldShowDistanceFilter = useMemo(() => {
    const ALLOWED_DISTANCE_TYPE_IDS = [
      1,  // AIRPORT - สนามบิน
      8,  // PARK - สวนสาธารณะ (GARDEN)
      9,  // HOSPITAL - โรงพยาบาล
      10, // MASS_TRANSIT - รถไฟฟ้า MRT/BTS
      11, // SHOPPING_MALL - ห้างสรรพสินค้า (MALL)
      17, // UNIVERSITY - มหาวิทยาลัย
      20  // ROAD - ถนน
    ];
    
    // 1. มี selectedLocation และ selectedSubLocation (กรณีปกติ)
    // 2. หรือ มี selectedLocation แต่ไม่มี selectedSubLocation (กรณีไม่มี option รอง)
    const hasValidLocation = selectedLocation && (selectedSubLocation || !selectedSubLocation);
    const hasValidTypeId = locationTypeId && ALLOWED_DISTANCE_TYPE_IDS.includes(locationTypeId);
    
    const isValidForDistanceFilter = selectedLocation && hasValidTypeId;
    
    return isValidForDistanceFilter;
  }, [selectedLocation, selectedSubLocation, locationTypeId]);

  const dynamicFilters = useMemo(() => {
    const filters: DynamicFilter[] = [];
    
    const ASANGHAA_ALLOWED_FILTERS = ['ราคา', 'ช่วงราคา', 'ค่าเช่า/เดือน', 'ประกาศโดย', 'ลงประกาศเมื่อ'];
    const isAsanghaCategory = selectedCategory === 'อสังหา';
    
    
    // 1. เพิ่ม filters จาก API
    filtersData.forEach(group => {
      const groupNameTh = group.group_name_th || group.group_name;
      
      if (isAsanghaCategory && !ASANGHAA_ALLOWED_FILTERS.includes(groupNameTh)) {
        return;
      }
      
      if (groupNameTh === 'ระยะห่าง' && !shouldShowDistanceFilter) {
        return;
      }
      
      const componentType = FILTER_COMPONENT_MAP[groupNameTh as keyof typeof FILTER_COMPONENT_MAP] || 'generic';
      
      let filterOrder = group.order;
      
      if (groupNameTh === 'ราคา' || groupNameTh === 'ช่วงราคา' || groupNameTh === 'ค่าเช่า/เดือน') {
        filterOrder = 1; // ราคาอยู่ด้านบนสุดเสมอ
      } else if (groupNameTh === 'ประกาศโดย') {
        filterOrder = 998; // ประกาศโดยอยู่ด้านล่าง
      } else if (groupNameTh === 'ลงประกาศเมื่อ') {
        filterOrder = 999; // ลงประกาศเมื่ออยู่ด้านล่างสุด
      } else {
        filterOrder = 10 + group.order;
      }
      
      if (isAsanghaCategory) {
        const asanghaOrderMap = {
          'ราคา': 1,
          'ประกาศโดย': 2,
          'ลงประกาศเมื่อ': 3
        };
        filterOrder = asanghaOrderMap[groupNameTh as keyof typeof asanghaOrderMap] || filterOrder;
      }

      
      filters.push({
        group: {
          ...group,
          order: filterOrder
        },
        componentType,
        order: filterOrder
      });
    });
    
    // 2. เพิ่ม fallback filters ที่ไม่มีใน API 
    const apiFilterNames = new Set(
      filtersData.map(g => FILTER_COMPONENT_MAP[g.group_name_th as keyof typeof FILTER_COMPONENT_MAP])
        .filter(Boolean)
    );
    
    const fallbackFiltersToAdd = isAsanghaCategory 
      ? ['price', 'publishedBy', 'postedDate']  // อสังหาใช้ 3 filters หลัก
      : fallbackFilters;
    
    
    fallbackFiltersToAdd.forEach((filterName, index) => {
      if (!apiFilterNames.has(filterName as any)) {
        const fallbackNameTh = getFallbackFilterNameTh(filterName);
        
        if (isAsanghaCategory && !ASANGHAA_ALLOWED_FILTERS.includes(fallbackNameTh)) {
          return;
        }
        
        let fallbackOrder = 50 + index; // default ตรงกลาง
        
        if (fallbackNameTh === 'ราคา' || fallbackNameTh === 'ช่วงราคา' || fallbackNameTh === 'ค่าเช่า/เดือน') {
          fallbackOrder = 1; // ราคาอยู่ด้านบนสุดเสมอ
        } else if (fallbackNameTh === 'ประกาศโดย') {
          fallbackOrder = 998; // ประกาศโดยอยู่ด้านล่าง
        } else if (fallbackNameTh === 'ลงประกาศเมื่อ') {
          fallbackOrder = 999; // ลงประกาศเมื่ออยู่ด้านล่างสุด
        }
        
        if (isAsanghaCategory) {
          const asanghaOrderMap = {
            'ราคา': 1,
            'ช่วงราคา': 1,
            'ประกาศโดย': 2,
            'ลงประกาศเมื่อ': 3
          };
          fallbackOrder = asanghaOrderMap[fallbackNameTh as keyof typeof asanghaOrderMap] || fallbackOrder;
        }
        
        const fakeGroup: FilterGroup = {
          group_id: -1 * (index + 1), // negative ID สำหรับ fallback
          group_name: filterName,
          group_name_th: fallbackNameTh,
          group_name_en: filterName,
          group_name_cn: filterName,
          order: fallbackOrder,
          options: []
        };

        
        const validComponentType: FilterComponentType = 
          Object.values(FILTER_COMPONENT_MAP).includes(filterName as any) 
            ? filterName as FilterComponentType 
            : 'generic';
          
        filters.push({
          group: fakeGroup,
          componentType: validComponentType,
          order: fallbackOrder
        });
      }
    });
    
    // 3. เรียงตาม order
    const sortedFilters = filters.sort((a, b) => a.order - b.order);
    
    return sortedFilters;
  }, [filtersData, fallbackFilters, selectedCategory, shouldShowDistanceFilter]);
  
  const getFilterLayout = (groupName: string): 'single' | 'double' => {
    return GENERIC_FILTER_LAYOUTS[groupName as keyof typeof GENERIC_FILTER_LAYOUTS] || 'double';
  };
  
  return {
    dynamicFilters,
    getFilterLayout
  };
};

function getFallbackFilterNameTh(filterName: string): string {
  const nameMap: Record<string, string> = {
    'price': 'ราคา',
    'bedroom': 'ห้องนอน', 
    'bathroom': 'ห้องน้ำ',
    'area': 'พื้นที่ใช้สอย',
    'quota': 'โควตา',
    'status': 'สถานะ',
    'floor': 'ชั้น',
    'view': 'วิว',
    'direction': 'ทิศ',
    'publishedBy': 'ประกาศโดย',
    'postedDate': 'ลงประกาศเมื่อ',
    'distance': 'ระยะห่าง'
  };
  
  return nameMap[filterName] || filterName;
} 